import {
  MANUFACTURER_PAYABLE_CATEGORIES,
  manufacturerOverageUsd,
  payablesCollectedUsdFromDealMetadata,
  type ManufacturerPayableCategory,
} from "@/lib/ops/manufacturerPayablesMath";
import type { DealRow } from "./opsPaperworkTypes";

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function OpsManufacturerPayableCreateForm(props: {
  deals: DealRow[];
  manufacturer: string;
  setManufacturer: (v: string) => void;
  manufacturerSuggestions: string[];
  category: ManufacturerPayableCategory;
  setCategory: (v: ManufacturerPayableCategory) => void;
  collected: string;
  setCollected: (v: string) => void;
  allowed: string;
  setAllowed: (v: string) => void;
  allowedDepositSuggestions: number[];
  dealId: string;
  setDealId: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;
  busy: boolean;
  onCreate: () => void;
}) {
  const previewOwed = manufacturerOverageUsd(Number(props.collected) || 0, Number(props.allowed) || 0);
  const hostCollectedHint =
    payablesCollectedUsdFromDealMetadata(props.deals.find((d) => d.id === props.dealId)?.metadata) != null;

  return (
    <form
      className="grid gap-2 rounded-xl border border-gray-200 p-3 sm:grid-cols-2 lg:grid-cols-4"
      onSubmit={(e) => {
        e.preventDefault();
        props.onCreate();
      }}
    >
      <label className="text-xs">
        Manufacturer
        <input
          className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          list="mfg-payable-manufacturers"
          value={props.manufacturer}
          onChange={(e) => props.setManufacturer(e.target.value)}
          placeholder="Type manufacturer name"
          required
        />
        <datalist id="mfg-payable-manufacturers">
          {props.manufacturerSuggestions.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      </label>
      <label className="text-xs">
        Category
        <select
          className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          value={props.category}
          onChange={(e) => props.setCategory(e.target.value as ManufacturerPayableCategory)}
        >
          {MANUFACTURER_PAYABLE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs">
        Collected from customer
        <input
          className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          type="number"
          min={0}
          step="0.01"
          value={props.collected}
          onChange={(e) => props.setCollected(e.target.value)}
          placeholder="12000"
          required
        />
        {hostCollectedHint ? (
          <span className="mt-0.5 block text-[11px] text-gray-500">
            Prefills from this deal’s host initial payment (or total)
          </span>
        ) : null}
      </label>
      <label className="text-xs">
        Allowed dealer deposit
        <input
          className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          type="number"
          min={0}
          step="0.01"
          value={props.allowed}
          onChange={(e) => props.setAllowed(e.target.value)}
          placeholder="1500"
          required
          list="mfg-payable-allowed-deposits"
        />
        {props.allowedDepositSuggestions.length > 0 ? (
          <datalist id="mfg-payable-allowed-deposits">
            {props.allowedDepositSuggestions.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        ) : null}
      </label>
      <label className="text-xs sm:col-span-2">
        Linked deal (optional)
        <select
          className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          value={props.dealId}
          onChange={(e) => props.setDealId(e.target.value)}
        >
          <option value="">None</option>
          {props.deals.map((d) => (
            <option key={d.id} value={d.id}>
              {d.display_name || d.id.slice(0, 8)}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs sm:col-span-2">
        Notes
        <input
          className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          value={props.notes}
          onChange={(e) => props.setNotes(e.target.value)}
          placeholder="Full pay up front / over-deposit"
        />
      </label>
      <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
        <p className="text-sm text-gray-600">
          Owed to manufacturer: <span className="font-semibold">{money(previewOwed)}</span>
        </p>
        <button
          type="submit"
          disabled={props.busy}
          className="ml-auto rounded bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          Add payable
        </button>
      </div>
    </form>
  );
}
